import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  LuAlertCircle,
  LuBadgeCheck,
  LuFlag,
  LuFlaskConical,
  LuHourglass,
  LuInfo,
  LuMessagesSquare,
  LuRefreshCw,
  LuUserCheck,
  LuUsers,
  LuDollarSign,
  LuWallet,
} from "react-icons/lu";
import apiClient from "../../api-services/ApiClient";
import AdminLayout from "../../app-components/AdminLayout";
import PageHeader from "../../app-components/ui/PageHeader";
import { inputBase } from "../../app-components/ui/styles";
import { SITELINKS } from "../../app-routes/links";
import { DashboardPeriod, DashboardSummary, MoneyStat } from "../../interface/dashboard.interface";
import { getErrorMessage } from "../../utils";
import FunnelCard from "./components/FunnelCard";
import LocationsCard from "./components/LocationsCard";
import PlansCard from "./components/PlansCard";
import { RecentSignups, RecentTransactions } from "./components/RecentActivity";
import RequestsCard from "./components/RequestsCard";
import RevenueChart from "./components/RevenueChart";
import SignupsChart from "./components/SignupsChart";
import StatCard from "./components/StatCard";
import UserStatusCard from "./components/UserStatusCard";
import { fmtCount, fmtMoney, Money, pctChange, pctOf } from "./components/format";
import { normalizeDashboard, num, PERIODS, PREVIEW_ALLOWED, SAMPLE_DASHBOARD } from "./dashboardData";

const Dashboard = () => {
  const [params] = useSearchParams();
  const preview = PREVIEW_ALLOWED && params.get("preview") === "1";

  const [period, setPeriod] = useState<DashboardPeriod>("30d");
  const [data, setData] = useState<DashboardSummary>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // The API echoes `period` back once it supports date ranges; until then it
  // returns all-time figures, so the period picker is replaced by an "All time" label.
  const [periodSupported, setPeriodSupported] = useState(false);
  // the Refresh button asks the API to skip its 5-minute cache
  const forceFresh = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (preview) {
        setData(SAMPLE_DASHBOARD);
        setPeriodSupported(true);
      } else {
        const fresh = forceFresh.current;
        forceFresh.current = false;
        const res: any = await apiClient.get("dashboard", {
          params: fresh ? { period, refresh: 1 } : { period },
        });
        setData(normalizeDashboard(res?.data));
        setPeriodSupported(res?.data?.period === period);
      }
      setUpdatedAt(new Date());
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [period, preview]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const users = data.users ?? {};
  const revenue = data.revenue ?? {};
  const subs = data.subscriptions ?? {};
  const periodLabel = PERIODS.find((p) => p.value === period)?.label.toLowerCase();

  // Revenue cards: period amount when the API sends it, otherwise the all-time total
  const moneyCard = (cur: Money, m: MoneyStat = {}) => {
    const hasPeriod = m.period !== undefined;
    const tx =
      m.transactions !== undefined
        ? ` · ${fmtCount(m.transactions)} ${m.transactions === 1 ? "payment" : "payments"}`
        : "";
    return {
      value: fmtMoney(cur, hasPeriod ? m.period : m.total, true),
      delta: hasPeriod ? pctChange(m.period, m.prev_period) : undefined,
      hint: hasPeriod
        ? `${period === "all" ? "All time" : `In the ${periodLabel}`}${tx}`
        : m.total !== undefined
        ? "All time"
        : undefined,
    };
  };
  const ngn = moneyCard("NGN", revenue.ngn);
  const usd = moneyCard("USD", revenue.usd);

  const requests = data.requests ?? {};
  const requestTotal =
    requests.total ??
    (requests.by_status
      ? Object.values(requests.by_status).reduce((a, b) => a + (Number(b) || 0), 0)
      : requests.by_gender
      ? Object.values(requests.by_gender).reduce((a, b) => a + (Number(b) || 0), 0)
      : undefined);
  const pendingRequests = num(requests.by_status?.pending);

  const activePct = pctOf(users.active, users.total);
  const firstLoad = loading && !updatedAt;

  return (
    <AdminLayout title="Admin | Dashboard">
      <PageHeader
        title="Dashboard"
        subtitle={
          updatedAt
            ? `Updated ${updatedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}${
                periodSupported ? "" : " · All-time figures"
              }`
            : "Loading the latest numbers"
        }
        actions={
          <>
            {periodSupported ? (
              <>
                <label className="sr-only" htmlFor="dash-period">
                  Period
                </label>
                <select
                  id="dash-period"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
                  className={`${inputBase} py-2`}
                >
                  {PERIODS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <span
                className="inline-flex h-10 items-center gap-2 rounded-lg border border-hcBorder bg-white px-3 text-sm text-honeConnectGray"
                title="Date ranges aren't supported by the dashboard API yet, so every figure is all time."
              >
                All time
                <LuInfo className="h-4 w-4 text-hcMuted" aria-hidden />
                <span className="sr-only">
                  (date ranges aren't supported by the dashboard API yet)
                </span>
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                forceFresh.current = true;
                setRefreshKey((k) => k + 1);
              }}
              disabled={loading}
              aria-label="Refresh dashboard"
              title="Refresh"
              className="flex h-10 w-10 items-center justify-center rounded-lg border border-hcBorder bg-white text-honeConnectGray transition-colors hover:bg-hcBg hover:text-honeConnectBlack disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-honeConnectPurple/40"
            >
              <LuRefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden />
            </button>
          </>
        }
      />

      {preview && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <LuFlaskConical className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <p>
            <span className="font-semibold">Preview mode.</span> Charts show sample numbers, not real data
            (the latest users and transactions lists are real).{" "}
            <Link to={SITELINKS.DASHBOARD} className="font-semibold underline">
              Show live data
            </Link>
          </p>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <LuAlertCircle className="h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1">Couldn't load the dashboard: {error}</span>
          <button
            type="button"
            onClick={() => setRefreshKey((k) => k + 1)}
            className="font-semibold underline"
          >
            Try again
          </button>
        </div>
      )}

      {/* ---------- KPIs ---------- */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <StatCard
          label="Total users"
          icon={LuUsers}
          value={fmtCount(users.total)}
          delta={pctChange(users.new_in_period, users.new_prev_period)}
          hint={
            users.new_in_period !== undefined
              ? period === "all"
                ? "All registered users"
                : `+${fmtCount(users.new_in_period)} new in the ${periodLabel}`
              : "All registered users"
          }
          to={SITELINKS.ELITE_USER}
          loading={firstLoad}
        />
        <StatCard
          label="Active users"
          icon={LuUserCheck}
          tone="green"
          value={fmtCount(users.active)}
          hint={activePct !== undefined ? `${activePct.toFixed(0)}% of all users` : undefined}
          to={`${SITELINKS.ELITE_USER}?status=ACTIVE`}
          loading={firstLoad}
        />
        <StatCard
          label="Pending approval"
          icon={LuHourglass}
          tone="amber"
          value={fmtCount(users.pending)}
          hint="Waiting for review"
          to={`${SITELINKS.ELITE_USER}?status=PENDING`}
          loading={firstLoad}
        />
        <StatCard
          label="Flagged users"
          icon={LuFlag}
          tone="red"
          value={fmtCount(users.flagged)}
          hint={users.removed !== undefined ? `${fmtCount(users.removed)} removed` : undefined}
          to={`${SITELINKS.ELITE_USER}?status=FLAGGED`}
          loading={firstLoad}
        />
        <StatCard
          label="Revenue (NGN)"
          icon={LuWallet}
          tone="purple"
          value={ngn.value}
          delta={ngn.delta}
          hint={ngn.hint}
          to={`${SITELINKS.TRANSACTIONS}?status=PAID`}
          loading={firstLoad}
        />
        <StatCard
          label="Revenue (USD)"
          icon={LuDollarSign}
          tone="blue"
          value={usd.value}
          delta={usd.delta}
          hint={usd.hint}
          to={`${SITELINKS.TRANSACTIONS}?status=PAID`}
          loading={firstLoad}
        />
        <StatCard
          label="Active subscriptions"
          icon={LuBadgeCheck}
          tone="slate"
          value={fmtCount(subs.active)}
          hint={subs.expiring_soon !== undefined ? `${fmtCount(subs.expiring_soon)} ending in 7 days` : undefined}
          loading={firstLoad}
        />
        <StatCard
          label="Connection requests"
          icon={LuMessagesSquare}
          tone="amber"
          value={fmtCount(requestTotal)}
          hint={pendingRequests !== undefined ? `${fmtCount(pendingRequests)} pending` : "All time"}
          to={SITELINKS.REQUEST}
          loading={firstLoad}
        />
      </div>

      {/* ---------- growth ---------- */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <SignupsChart data={data.signups_series} className="xl:col-span-2" />
        <UserStatusCard data={data} />
      </div>

      {/* ---------- locations ---------- */}
      <div className="mt-5">
        <LocationsCard countries={data.users_by_country} cities={data.users_by_city} />
      </div>

      {/* ---------- money ---------- */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <RevenueChart data={data.revenue_series} className="xl:col-span-2" />
        <PlansCard data={data} />
      </div>

      {/* ---------- engagement ---------- */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <FunnelCard data={data.funnel} />
        <RequestsCard data={data} />
      </div>

      {/* ---------- latest activity (existing list endpoints) ---------- */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <RecentSignups refreshKey={refreshKey} />
        <RecentTransactions refreshKey={refreshKey} />
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
